import { Timestamp } from 'firebase/firestore';

// Matches iOS UserRole raw values — 'user' is the regular role
export enum UserRole {
  REGULAR = 'user',
  ADMIN = 'admin'
}

export interface User {
  id: string;
  email: string;
  displayName: string;
  fullName?: string;
  phoneNumber?: string;
  role: UserRole;
  createdAt: Date;
  isActive: boolean;
  trialEndDate?: Date;
  isTrialActive: boolean;
  subscriptionStatus?: string; // 'active', 'expired'
  subscriptionType?: string; // 'monthly', 'yearly'
  subscriptionEndDate?: Date;
  fcmToken?: string;
}

export interface UserDocumentData {
  id: string;
  email: string;
  displayName: string;
  fullName?: string;
  phoneNumber?: string;
  role: string;
  createdAt: Date | Timestamp;
  isActive: boolean;
  trialEndDate?: Date | Timestamp;
  isTrialActive: boolean;
  subscriptionStatus?: string;
  subscriptionType?: string;
  subscriptionEndDate?: Date | Timestamp;
  fcmToken?: string;
}

const toDate = (value?: Date | Timestamp): Date | undefined => {
  if (!value) return undefined;
  return value instanceof Timestamp ? value.toDate() : new Date(value);
};

export class UserModel {
  static fromFirestore(data: UserDocumentData): User {
    return {
      id: data.id,
      email: data.email,
      displayName: data.displayName,
      fullName: data.fullName,
      phoneNumber: data.phoneNumber,
      role: data.role as UserRole,
      createdAt: toDate(data.createdAt) ?? new Date(),
      isActive: data.isActive,
      trialEndDate: toDate(data.trialEndDate),
      isTrialActive: data.isTrialActive,
      subscriptionStatus: data.subscriptionStatus,
      subscriptionType: data.subscriptionType,
      subscriptionEndDate: toDate(data.subscriptionEndDate),
      fcmToken: data.fcmToken
    };
  }

  static isAdmin(user: User | null): boolean {
    return user?.role === UserRole.ADMIN;
  }

  static hasActiveSubscription(user: User | null): boolean {
    if (!user) return false;
    if (user.subscriptionEndDate && user.subscriptionEndDate > new Date()) {
      return true;
    }
    return (user.subscriptionStatus ?? '').toLowerCase() === 'active';
  }

  // Mirrors iOS AuthViewModel.hasValidAccess()
  static hasValidAccess(user: User | null): boolean {
    if (!user) return false;
    if (UserModel.isAdmin(user)) return true;
    if (user.role === UserRole.REGULAR && user.isTrialActive) {
      if (!user.trialEndDate) return false;
      return new Date() <= user.trialEndDate;
    }
    return false;
  }

  static trialDaysRemaining(user: User | null): number {
    if (!user?.trialEndDate) return 0;
    const ms = user.trialEndDate.getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  }
}
